// src/components/Subscription/Checkout.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Typography
} from '@mui/material';
import { loadStripe } from '@stripe/stripe-js';
import api from '../../services/api';

// Load Stripe only when publishable key is configured
const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

const Checkout = () => {
  const { plan } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  
  const steps = ['Select plan', 'Review', 'Payment'];
  
  // Plan details based on the selected plan
  const planDetails = {
    premium: {
      name: 'Premium Plan',
      price: '€4.99',
      billingCycle: 'monthly',
      features: [
        'Unlimited budget categories',
        'AI-powered spending insights',
        'Customizable reports and charts',
        'Savings goals and tracking',
        'Bill payment reminders',
        'Export data to CSV/Excel',
        'Priority support'
      ]
    },
    family: {
      name: 'Family Plan',
      price: '€9.99',
      billingCycle: 'monthly',
      features: [
        'All Premium features',
        'Up to 5 user accounts',
        'Shared budget categories',
        'Role-based permissions',
        'Household spending analytics',
        'Financial goal collaboration',
        'Annual financial review'
      ]
    }
  };
  
  // Ensure we have a valid plan
  useEffect(() => {
    if (!planDetails[plan]) {
      navigate('/subscription/plans');
    } else {
      setActiveStep(1);  // Skip the first step since they already selected a plan
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan, navigate]);
  
  const handleCheckout = async () => {
    if (!stripePromise) {
      setError('Payment system is being configured. Please try again later.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // Call backend to create a Checkout Session — backend resolves price IDs from env vars
      const response = await api.post('/subscription/checkout', {
        planId: plan,
        billingCycle: planDetails[plan].billingCycle
      });
      
      const { sessionId, sessionUrl } = response.data?.data || response.data || {};
      
      // Prefer Stripe-hosted URL (no client-side Stripe instance needed)
      if (sessionUrl) {
        window.location.href = sessionUrl;
        return;
      }

      // Fallback: use Stripe.js redirect if only sessionId is returned
      if (sessionId) {
        const stripe = await stripePromise;
        const result = await stripe.redirectToCheckout({ sessionId });
        if (result.error) {
          setError(result.error.message);
        }
      } else {
        setError('Unable to create checkout session. Please try again.');
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Payment processing failed. Please try again.';
      setError(message);
      console.error('Checkout error:', error);
    } finally {
      setLoading(false);
    }
  };
  
  if (!planDetails[plan]) {
    return null;  // Will redirect via useEffect
  }

  // Show user-friendly error if Stripe publishable key is not set
  if (!stripePromise) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Payment system is being configured. Please try again later.
        </Alert>
        <Button variant="text" onClick={() => navigate('/subscription/plans')}>
          Back to Plans
        </Button>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          Checkout: {planDetails[plan].name}
        </Typography>
        
        <Divider sx={{ my: 3 }} />
        
        <Grid container spacing={4}>
          <Grid item xs={12} md={7}>
            <Typography variant="h6" gutterBottom>
              Order Summary
            </Typography>
            
            <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {planDetails[plan].name}
                </Typography>
                <Typography variant="subtitle1" fontWeight={600}>
                  {planDetails[plan].price}/{planDetails[plan].billingCycle}
                </Typography>
              </Box>
              
              <Divider sx={{ mb: 2 }} />
              
              <Typography variant="body2" color="text.secondary" gutterBottom>
                You'll be charged {planDetails[plan].price} per month until you cancel.
                You can cancel anytime from your account settings.
              </Typography>
            </Card>
            
            <Box sx={{ mt: 4 }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                onClick={handleCheckout}
                disabled={loading}
                sx={{ py: 1.5, fontWeight: 600 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Proceed to Payment'}
              </Button>
              
              {error && (
                <Typography variant="body2" color="error" sx={{ mt: 2, textAlign: 'center' }}>
                  {error}
                </Typography>
              )}
              
              <Button
                variant="text"
                onClick={() => navigate('/subscription/plans')}
                sx={{ mt: 2 }}
              >
                Back to Plans
              </Button>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={5}>
            <Typography variant="h6" gutterBottom>
              What's Included
            </Typography>
            
            <List>
              {planDetails[plan].features.map((feature) => (
                <ListItem key={feature} sx={{ py: 0.5 }}>
                  <ListItemText 
                    primary={feature} 
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle2" gutterBottom>
              Subscription for: {user?.email}
            </Typography>
            
            <Typography variant="body2" color="text.secondary">
              Your subscription will start immediately after payment.
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default Checkout;